import { Work } from "@biblioteksentralen/cordata";
import { getErrorMessage } from "@libry-content/common";
import { defaultSizes } from "./constants";
import { SiteSearchConfig } from "./searchConfig";
import { bmdbFetcher, LCWork } from "@libry-content/integrations";
import { PlasFacet, SearchWorksParams } from "@biblioteksentralen/bmdb-search-client";
import { sift } from "radash";

export type WorkSearchResult = Work & { representativeManifestationId?: string };

export const searchWorks = async (
  siteSearchConfig: SiteSearchConfig,
  searchWorksParams: SearchWorksParams
): Promise<{
  works: LCWork[];
  facets?: PlasFacet[];
  endOfResults: boolean;
}> => {
  try {
    const { isSearchIntegrationEnabled, libraryCatalogueId } = siteSearchConfig;
    if (!isSearchIntegrationEnabled || !libraryCatalogueId) {
      throw new Error("Site settings: Search integration is not enabled for this site");
    }

    const { size = defaultSizes.works } = searchWorksParams;
    const responseData = await bmdbFetcher({
      operation: "searchWorks",
      params: {
        size,
        ...searchWorksParams,
      },
      siteSearchConfig,
    });

    if (!responseData) {
      throw new Error(
        `Unexpected works search response with ${
          searchWorksParams.query ? `search query "${searchWorksParams.query}` : "no search query"
        }": response = ${JSON.stringify(responseData)}`
      );
    }

    const works = responseData?.results.map(
      (result) =>
        new LCWork({
          work: result.work,
          representativeManifestationId: result.representativeManifestationId,
        })
    );

    const { facets, endOfResults } = responseData;

    return { works: works, facets, endOfResults };
  } catch (err) {
    console.error(`Could not search works: ${getErrorMessage(err)}`);
    return { works: [], facets: [], endOfResults: true };
  }
};

export const getWorks = async (
  siteSearchConfig: SiteSearchConfig,
  workIds: string[],
  useGlobalCatalogue?: boolean
): Promise<LCWork[]> => {
  try {
    if (!siteSearchConfig.isSearchIntegrationEnabled) {
      throw new Error("Site settings: Search integration is not enabled for this site");
    }

    if (workIds.length === 0) return [];

    // getWorks api endpoint only delivers 10 works in one call, so to fetch more we divvy up work ids into chunks of 10
    const nChunks = Math.ceil(workIds.length / 10);
    const workIdsBatches = new Array(nChunks)
      .fill(undefined)
      .map((chunk, index) => workIds.slice(index * 10, (index + 1) * 10));

    const responseData = await Promise.all(
      workIdsBatches.flatMap((workIdsBatch) =>
        bmdbFetcher({
          operation: "getWorks",
          params: { work_ids: workIdsBatch },
          siteSearchConfig,
          useGlobalCatalogue,
        })
      )
    );

    if (responseData?.find((worksBatch) => !worksBatch))
      throw new Error(`Could not fetch works of IDs: ${workIds.join(", ")}`);

    const worksArray = sift(
      responseData.flatMap(
        (worksChunk) => worksChunk && Object.values(worksChunk.works).map((work) => new LCWork({ work }))
      )
    );

    return worksArray;
  } catch (err) {
    console.error(getErrorMessage(err));
    return [];
  }
};

export const getWork = async (
  siteSearchConfig: SiteSearchConfig,
  workId: string,
  useGlobalCatalogue?: boolean
): Promise<LCWork | undefined> => {
  const work = (await getWorks(siteSearchConfig, [workId], useGlobalCatalogue))[0];
  if (!work) {
    console.warn(`Could not fetch work of ID: ${workId}`);
    return;
  }
  return work;
};
